import { apiEndpoints } from '@config/env';
import { deleteData, getData, postData } from '../utils/global';
import { debug, warn } from '../utils/console';

const SELECTED_SOURCES_PARENT = 'selected_sources';

const saveInflightByKey = new Map<string, Promise<void>>();

function buildSelectedSourcesPersistKey(
  className: string,
  projectId: number,
  userId: string
): string {
  return `${normalizeClassName(className)}:${projectId}:${userId}:${SELECTED_SOURCES_PARENT}:${objectNameForProject(projectId)}`;
}

async function runSerializedSelectedSourcesSave(
  key: string,
  operation: () => Promise<void>
): Promise<void> {
  const previous = saveInflightByKey.get(key);
  const current = (async () => {
    if (previous) {
      try {
        await previous;
      } catch {
        // Prior failure must not block the next save attempt.
      }
    }
    await operation();
  })();

  saveInflightByKey.set(key, current);
  try {
    await current;
  } finally {
    if (saveInflightByKey.get(key) === current) {
      saveInflightByKey.delete(key);
    }
  }
}

export interface SelectedSourcesPayload {
  project_id: number;
  source_names: string[];
}

interface SelectedSourcesParams {
  userId: string;
  className: string;
  projectId: number | string;
}

function objectNameForProject(projectId: number | string): string {
  return `project_${Number(projectId)}`;
}

function cleanSourceNames(sourceNames: readonly string[]): string[] {
  return sourceNames.map((name) => String(name).trim()).filter(Boolean);
}

function parseSelectedSourcesPayload(payload: unknown, expectedProjectId: number): string[] | null {
  let data: unknown = payload;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof data === 'object' && data !== null && 'value' in data && (data as { value?: unknown }).value != null) {
    data = (data as { value: unknown }).value;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return null;
      }
    }
  }
  if (!data || typeof data !== 'object') {
    return null;
  }

  const candidate = data as Partial<SelectedSourcesPayload>;
  if (candidate.project_id != null && Number(candidate.project_id) !== expectedProjectId) {
    warn('[SelectedSources] Ignoring user_object with mismatched project_id', {
      expectedProjectId,
      payloadProjectId: candidate.project_id,
    });
    return null;
  }
  if (!Array.isArray(candidate.source_names)) {
    return null;
  }

  const names = cleanSourceNames(candidate.source_names);
  return names.length > 0 ? names : null;
}

function normalizeClassName(className: string): string {
  return String(className ?? '').trim().toLowerCase();
}

function buildObjectUrl(params: SelectedSourcesParams): string {
  const className = normalizeClassName(params.className);
  return `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(params.projectId)}&user_id=${encodeURIComponent(params.userId)}&parent_name=${encodeURIComponent(SELECTED_SOURCES_PARENT)}&object_name=${encodeURIComponent(objectNameForProject(params.projectId))}`;
}

/**
 * Load persisted fleet source selection for a user+project from user_objects.
 * Returns null when no object exists or payload is empty/invalid.
 */
export async function loadSelectedSources(
  className: string,
  projectId: number,
  userId: string
): Promise<string[] | null> {
  if (!userId || !className || !projectId) {
    return null;
  }

  try {
    const response = await getData(buildObjectUrl({ userId, className, projectId }));
    const statusCode = (response as { statusCode?: number }).statusCode;
    if (!response?.success || statusCode === 204 || response.data == null) {
      return null;
    }

    const names = parseSelectedSourcesPayload(response.data, projectId);
    debug('[SelectedSources] Loaded from user_objects', {
      projectId,
      sourceCount: names?.length ?? 0,
    });
    return names;
  } catch (error) {
    warn('[SelectedSources] Failed to load from user_objects', error);
    return null;
  }
}

/**
 * Persist a non-empty source subset. Empty or full-catalog selections delete the object
 * so the project reverts to the all-sources default.
 */
export async function saveSelectedSources(
  className: string,
  projectId: number,
  userId: string,
  sourceNames: readonly string[],
  catalogNames?: readonly string[]
): Promise<void> {
  if (!userId || !className || !projectId) {
    return;
  }

  const persistKey = buildSelectedSourcesPersistKey(className, projectId, userId);
  await runSerializedSelectedSourcesSave(persistKey, async () => {
    const cleaned = cleanSourceNames(sourceNames);
    if (cleaned.length === 0) {
      await deleteSelectedSources(className, projectId, userId);
      return;
    }

    if (catalogNames && catalogNames.length > 0) {
      const catalogSet = new Set(catalogNames.map((name) => String(name).trim().toLowerCase()).filter(Boolean));
      const selectedSet = new Set(cleaned.map((name) => name.toLowerCase()));
      if (selectedSet.size === catalogSet.size && [...selectedSet].every((name) => catalogSet.has(name))) {
        await deleteSelectedSources(className, projectId, userId);
        return;
      }
    }

    const payload: SelectedSourcesPayload = {
      project_id: projectId,
      source_names: cleaned,
    };

    const body = {
      class_name: normalizeClassName(className),
      project_id: projectId,
      user_id: userId,
      parent_name: SELECTED_SOURCES_PARENT,
      object_name: objectNameForProject(projectId),
      json: JSON.stringify(payload),
    };

    const response = await postData(`${apiEndpoints.app.users}/object`, body);
    if (!response?.success) {
      warn('[SelectedSources] Save to user_objects failed', response?.message || response?.error);
      return;
    }

    debug('[SelectedSources] Saved to user_objects', {
      projectId,
      sourceCount: cleaned.length,
    });
  });
}

/** Remove persisted selection so project defaults to all available sources. */
export async function deleteSelectedSources(
  className: string,
  projectId: number,
  userId: string
): Promise<void> {
  if (!userId || !className || !projectId) {
    return;
  }

  const body = {
    class_name: normalizeClassName(className),
    project_id: projectId,
    user_id: userId,
    parent_name: SELECTED_SOURCES_PARENT,
    object_name: objectNameForProject(projectId),
  };

  try {
    const response = await deleteData(`${apiEndpoints.app.users}/object`, body);
    if (!response?.success && (response as { statusCode?: number }).statusCode !== 404) {
      warn('[SelectedSources] Delete from user_objects failed', response?.message || response?.error);
      return;
    }
    debug('[SelectedSources] Deleted user_object (revert to all-sources default)', { projectId });
  } catch (error) {
    warn('[SelectedSources] Failed to delete user_object', error);
  }
}
